import React, { useEffect, useState } from "react"
import { bindActionCreators } from "redux"
import { connect } from "react-redux"
import { Form, DatePicker, Select, Button, theme as themeConfig } from "antd"
import { UndoOutlined } from "@ant-design/icons"
import dayjs from "dayjs"

import { removeUpdatedFlag } from "../../../../../../data/redux/campaign_analytics_details/actions"

import { formatAndBindToJSONToDayjs } from "../../../../../../data/utils/common"

const RefreshFilters = ({
  open,
  onClose,
  onRefresh,
  actions,
  loading = false,
  styles,
  selectedDateRange,
  selectedCampaignIds,
  granularity,
  item,
}) => {
  const { token } = themeConfig.useToken()
  const { removeUpdatedFlag } = actions
  const [form] = Form.useForm()
  const [hasChanges, setHasChanges] = useState(false)

  // Get initial values from item.refreshComponent.filters if available, otherwise use global state
  const getInitialValues = () => {
    let dateRange = null

    // Convert startDate/endDate to dateRange array if available
    if (
      item?.refreshComponent?.filters?.startDate &&
      item?.refreshComponent?.filters?.endDate
    ) {
      dateRange = [
        dayjs(item.refreshComponent.filters.startDate),
        dayjs(item.refreshComponent.filters.endDate),
      ]
    } else {
      dateRange = selectedDateRange
    }

    // Handle campaignId - prioritize item.refreshComponent.filters, ensure it's always an array
    let campaignId = []
    if (item?.refreshComponent?.filters?.campaignId) {
      // If item has campaignId, use it (convert to array if it's a string)
      campaignId = Array.isArray(item.refreshComponent.filters.campaignId)
        ? item.refreshComponent.filters.campaignId
        : [item.refreshComponent.filters.campaignId]
    } else {
      // Otherwise use selectedCampaignIds
      campaignId = selectedCampaignIds || []
    }

    return {
      dateRange: dateRange,
      granularity: item?.refreshComponent?.filters?.granularity || granularity,
      campaignId: campaignId,
    }
  }

  const initialValues = getInitialValues()

  // Set initial values when component opens
  useEffect(() => {
    if (open) {
      const values = getInitialValues()
      form.setFieldsValue(values)
      setHasChanges(false) // Reset changes flag when drawer opens
    }
  }, [open, item, selectedCampaignIds, selectedDateRange, granularity, form])

  // Check if form values have changed
  const checkForChanges = () => {
    const currentValues = form.getFieldsValue()
    const initialVals = getInitialValues()

    // Compare current values with initial values
    const dateRangeChanged =
      JSON.stringify(currentValues.dateRange) !==
      JSON.stringify(initialVals.dateRange)
    const granularityChanged =
      currentValues.granularity !== initialVals.granularity
    const campaignIdChanged =
      JSON.stringify(currentValues.campaignId) !==
      JSON.stringify(initialVals.campaignId)

    const hasFormChanges =
      dateRangeChanged || granularityChanged || campaignIdChanged
    setHasChanges(hasFormChanges)
  }

  const handleRefresh = values => {
    // Only proceed if there are changes
    if (!hasChanges) return

    let formattedStart = null
    let formattedEnd = null

    if (values.dateRange && values.dateRange.length === 2) {
      const [start, end] = values.dateRange
      formattedStart = formatAndBindToJSONToDayjs(start, "current")
      formattedEnd = formatAndBindToJSONToDayjs(end, "current")
    }

    const refreshData = {
      id: item?.id,
      startDate: formattedStart,
      endDate: formattedEnd,
      granularity: values.granularity,
      campaignId: values.campaignId,
    }

    if (onRefresh) {
      onRefresh(refreshData)
    }
    onClose()
  }

  const handleReset = () => {
    // Reset to global state values
    const resetValues = {
      dateRange: selectedDateRange,
      granularity: granularity,
      campaignId: selectedCampaignIds || [],
    }

    form.setFieldsValue(resetValues)
    setHasChanges(false)

    let formattedStart = null
    let formattedEnd = null

    if (selectedDateRange && selectedDateRange.length === 2) {
      const [start, end] = selectedDateRange
      formattedStart = formatAndBindToJSONToDayjs(start, "current")
      formattedEnd = formatAndBindToJSONToDayjs(end, "current")
    }

    const refreshData = {
      id: item?.id,
      startDate: formattedStart,
      endDate: formattedEnd,
      granularity: granularity,
      campaignId: selectedCampaignIds || [],
    }

    if (onRefresh) {
      onRefresh(refreshData, () => {
        removeUpdatedFlag(item?.id)
      })
    }

    onClose()
  }

  // Convert campaigns list to options format - prioritize item.refreshComponent.filters.campaignId
  const getCampaignOptions = () => {
    let campaignIds = []

    if (item?.refreshComponent?.filters?.campaignId) {
      // If item has campaignId, use only that (ensure it's an array)
      campaignIds = Array.isArray(item.refreshComponent.filters.campaignId)
        ? item.refreshComponent.filters.campaignId
        : [item.refreshComponent.filters.campaignId]
    } else {
      // Otherwise use selectedCampaignIds
      campaignIds = selectedCampaignIds || []
    }

    return campaignIds.map(id => ({
      value: id,
      label: id,
    }))
  }

  const campaignOptions = getCampaignOptions()

  return (
    <>
      {/* Custom Side Drawer */}
      <div
        style={{
          position: "absolute",
          top: 0,
          right: open ? 0 : "-320px",
          width: "320px",
          height: "350px",
          backgroundColor: token.colorBgContainer,
          borderLeft: `1px solid ${token.colorBorderSecondary}`,
          boxShadow: open ? token.boxShadowSecondary : "none",
          transition: "right 0.3s ease-in-out",
          zIndex: 9999,
          display: "flex",
          flexDirection: "column",
          ...styles,
        }}
      >
        {/* Drawer Header */}
        <div
          style={{
            padding: token.paddingSM,
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>{"Update Data"}</div>
          <Button
            icon={<UndoOutlined />}
            size="small"
            type="primary"
            onClick={handleReset}
          >
            Reset
          </Button>
        </div>

        {/* Drawer Content */}
        <div
          style={{
            flex: 1,
            padding: token.padding,
            overflowY: "auto",
            overflowX: "visible",
          }}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleRefresh}
            initialValues={initialValues}
            onValuesChange={checkForChanges}
          >
            <Form.Item
              label="Date Range"
              name="dateRange"
              style={{
                marginBottom: token.marginSM,
              }}
            >
              <DatePicker.RangePicker
                size="small"
                showTime={{ format: "HH:mm" }}
                format="YYYY-MM-DD HH:mm"
                allowClear={true}
                style={{ minWidth: 260, width: "100%" }}
                placeholder={["Start Date & Time", "End Date & Time"]}
                popupStyle={{
                  zIndex: 10001,
                }}
              />
            </Form.Item>

            <Form.Item
              label="Granularity"
              name="granularity"
              style={{
                marginBottom: token.marginSM,
              }}
            >
              <Select
                style={{ width: "100%" }}
                placeholder="Select Granularity"
                size="small"
                allowClear
                options={[
                  { value: "auto", label: "Auto" },
                  { value: "minutes", label: "Minutes" },
                  { value: "hours", label: "Hours" },
                  { value: "days", label: "Days" },
                  { value: "weeks", label: "Weeks" },
                  { value: "months", label: "Months" },
                ]}
                dropdownStyle={{
                  zIndex: 10001,
                }}
              />
            </Form.Item>

            <Form.Item
              label="Campaign ID"
              name="campaignId"
              style={{
                marginBottom: token.marginSM,
              }}
            >
              <Select
                mode="tags"
                style={{ width: "100%" }}
                placeholder="Search and select campaigns"
                size="small"
                allowClear
                showSearch
                filterOption={false}
                notFoundContent={null}
                maxTagCount={0}
                maxTagPlaceholder={omittedValues =>
                  omittedValues.length > 0
                    ? `${omittedValues.length} campaign(s) selected`
                    : "Search and select campaigns"
                }
                options={campaignOptions}
                getPopupContainer={triggerNode => triggerNode.parentNode}
                dropdownStyle={{
                  zIndex: 10000,
                }}
              />
            </Form.Item>
          </Form>
        </div>

        {/* Drawer Footer */}
        <div
          style={{
            padding: token.padding,
            borderTop: `1px solid ${token.colorBorderSecondary}`,
            backgroundColor: token.colorBgElevated,
            display: "flex",
            justifyContent: "flex-end",
            gap: token.marginXS,
          }}
        >
          <Button size="small" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="primary"
            size="small"
            onClick={() => form.submit()}
            loading={loading}
            disabled={!hasChanges}
            title={!hasChanges ? "No changes made to apply" : ""}
          >
            Apply
          </Button>
        </div>
      </div>
    </>
  )
}

const mapStateToProps = state => ({
  selectedDateRange: state.campaigns_analytics_details.selectedDateRange,
  selectedCampaignIds: state.campaigns_analytics_details.selectedCampaignIds,
  granularity: state.campaigns_analytics_details.granularity,
})

const mapDispatchToProps = dispatch => {
  return {
    actions: bindActionCreators(
      {
        removeUpdatedFlag,
      },
      dispatch
    ),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(RefreshFilters)
